import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, getTopics, calculateCost, StatsResponse, TopicCount } from '../services/api';
import { useTheme } from '../ThemeContext';

function formatCost(usd: number): string {
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

interface StatsPageProps {
  sessionStart: string;
  sessionQueryCount: number;
}

export const StatsPage: React.FC<StatsPageProps> = ({ sessionStart, sessionQueryCount }) => {
  const navigate = useNavigate();
  const { mode, setMode } = useTheme();

  const [sessionStats, setSessionStats] = useState<StatsResponse | null>(null);
  const [topics, setTopics] = useState<TopicCount[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date().toISOString();
      const [sessionData, topicsData] = await Promise.all([
        getStats(sessionStart, now),
        getTopics(sessionStart, now),
      ]);
      setSessionStats(sessionData);
      setTopics(topicsData);
    } catch {
      setSessionStats(null);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [sessionStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const elapsed = Date.now() - new Date(sessionStart).getTime();
  const maxCount = topics.length > 0 ? topics[0].count : 1;

  const themeNext = mode === 'light' ? 'dark' : mode === 'dark' ? 'auto' : 'light';
  const themeIcon = mode === 'light' ? '☀️' : mode === 'dark' ? '🌙' : '⚙️';

  const statBoxStyle: React.CSSProperties = { textAlign: 'center', padding: '12px 0' };
  const statValue: React.CSSProperties = { fontSize: 24, fontWeight: 'bold', color: 'var(--text-primary)' };
  const statLabel: React.CSSProperties = { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 };
  const sectionStyle: React.CSSProperties = { background: 'var(--bg-info)', borderRadius: 10, padding: '16px 20px', marginBottom: 12 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 700, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', background: 'var(--header-bg)', color: '#fff', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="" style={{ width: 32, height: 32, cursor: 'pointer' }} onClick={() => navigate('/')} title="Späť na chat" />
          <div>
            <h1 style={{ margin: 0, fontSize: 18 }}>Štatistiky relácie</h1>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Aktuálna relácia</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => navigate('/stats/dashboard')} title="Dashboard" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, lineHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
          </button>
          <button onClick={() => setMode(themeNext)} title={themeNext} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, lineHeight: '32px' }}>{themeIcon}</button>
          <button onClick={() => navigate('/')} title="Späť na chat" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, lineHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-chat)' }}>

        {/* Session stats */}
        <div style={sectionStyle}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>Aktuálna relácia</div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 10, color: 'var(--text-muted)' }}>Načítavam...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              <div style={statBoxStyle}>
                <div style={statValue}>{formatDuration(elapsed)}</div>
                <div style={statLabel}>trvanie</div>
              </div>
              <div style={statBoxStyle}>
                <div style={statValue}>{sessionQueryCount}</div>
                <div style={statLabel}>otázok</div>
              </div>
              <div style={statBoxStyle}>
                <div style={statValue}>{sessionStats ? (sessionStats.totalInputTokens + sessionStats.totalOutputTokens).toLocaleString() : '—'}</div>
                <div style={statLabel}>tokenov</div>
              </div>
              <div style={statBoxStyle}>
                <div style={statValue}>{sessionStats ? formatCost(calculateCost(sessionStats.totalInputTokens, sessionStats.totalOutputTokens)) : '—'}</div>
                <div style={statLabel}>cena</div>
              </div>
            </div>
          )}
        </div>

        {/* Session topics */}
        {topics.length > 0 && (
          <div style={sectionStyle}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>Oblasti záujmu (relácia)</div>
            {topics.slice(0, 5).map((t) => (
              <div key={t.category} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 110, fontSize: 13, textAlign: 'right', flexShrink: 0, color: 'var(--text-primary)' }}>{t.category}</div>
                <div style={{ flex: 1, background: 'var(--border-color)', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                  <div style={{ width: `${(t.count / maxCount) * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
                <div style={{ width: 35, fontSize: 13, color: 'var(--text-muted)' }}>{t.count}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding: '10px 20px', background: 'var(--bg-footer)', color: 'var(--text-muted)',
        fontSize: 11, textAlign: 'center', borderTop: '1px solid var(--border-color)',
      }}>
        <span>🧪 Experiment #463.2026.4</span>
        <span style={{ margin: '0 4px' }}>—</span>
        <span>Built in{' '}<a href="https://lab463.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>lab463</a></span>
      </div>
    </div>
  );
};
